import type { CodexItem } from '@reados/contracts';

type Value = Record<string, unknown>;
const record = (value: unknown): Value => value && typeof value === 'object' && !Array.isArray(value) ? value as Value : {};
const text = (value: unknown) => typeof value === 'string' ? value.trim() : '';
const array = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

function reference(value: unknown): Value | undefined {
  const source = record(value), citation = record(source.url_citation ?? source.urlCitation);
  const rawURL = typeof value === 'string' ? value : source.url ?? citation.url;
  if (typeof rawURL !== 'string') return;
  try {
    const url = new URL(rawURL);
    if (!['https:', 'http:'].includes(url.protocol) || !url.hostname || url.username || url.password || rawURL.length > 8192) return;
    // A result's explicit URL identifies that result. Never generate a hosted
    // source ID from array order or reuse a tool-call ID as a citation identity.
    const identities = [source.sourceID, source.sourceId, source.source_id, source.referenceID, source.referenceId,
      source.reference_id, source.refID, source.refId, source.ref_id, ...array(source.sourceAliases),
      citation.sourceID, citation.sourceId, citation.source_id, citation.referenceID, citation.referenceId, citation.reference_id]
      .map(text).filter(Boolean);
    if (/^turn\d+[A-Za-z]+\d+$/.test(text(source.id))) identities.push(text(source.id));
    const ids = [...new Set(identities)];
    return { ...source, url: rawURL, title: text(source.title ?? source.name ?? citation.title) || url.hostname,
      content: text(source.content ?? source.snippet ?? source.description ?? source.text),
      ...(ids.length ? { sourceID: ids[0], sourceAliases: ids.slice(1) } : {}) };
  } catch { return; }
}

/** The stock native webSearch item remains intact in ChatMessage.items. This
 * adapts its explicit action and result fields into the copied ReadOS UI DTO. */
export function nativeWebSearchPresentation(item: CodexItem, state: 'running' | 'error' | 'success'): Value {
  const action = record(item.action), actionType = text(action.type);
  const queries = [...new Set([item.query, action.query, ...array(action.queries)].map(text).filter(Boolean))];
  const references = [...array(item.searchReferences), ...array(item.sources), ...array(item.results),
    ...array(action.sources)].flatMap(value => { const found = reference(value); return found ? [found] : []; });
  const uniqueReferences = [...new Map(references.map(value => [JSON.stringify([value.sourceID || '', value.url]), value])).values()];
  const operation = actionType === 'openPage' || actionType === 'open_page' ? '打开网页'
    : actionType === 'findInPage' || actionType === 'find_in_page' ? '查找网页内容' : '搜索网页';
  const details = operation === '搜索网页' ? queries.join('\n')
    : [text(action.url), text(action.pattern)].filter(Boolean).join('\n');
  return { type: 'web-search', tool: 'web_search', readexToolName: 'web_search',
    text: state === 'running' ? `正在${operation}` : state === 'error' ? `${operation}失败` : `已${operation}`,
    query: queries[0] || '', searchQueries: queries, searchReferences: uniqueReferences,
    webSearchActions: Object.keys(action).length ? [action] : [],
    ...(Object.keys(action).length ? { action, webSearchAction: action } : {}),
    arguments: { query: item.query, action: item.action }, result: item, detailText: details };
}
